import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRouteMatch, Switch } from "react-router-dom";
import { PrivateRoute } from "../../components/PrivateRoute";
import { useSelector } from "react-redux";
import { selectOrg } from "../../models/organization/organizationSlice";
import { selectMyUser } from "../../models/users/myUserSlice";
import Unauthorized from "../Unauthorized";
import api from "../../utils/api";
import { canAccess } from "../../utils/user";

interface AnalyticsEmbed {
  embedUrl: string;
  expiresAt: string;
}

/** Re-mint this long before the token expires so the iframe never blanks out. */
const REFRESH_LEAD_MS = 60_000;

const AnalyticsEmbedFrame = ({ orgSlug }: { orgSlug: string }) => {
  const [embedUrl, setEmbedUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>();

  const loadEmbed = useCallback(async () => {
    try {
      const { data } = await api.get<AnalyticsEmbed>(
        `/organizations/${orgSlug}/analytics/embed`
      );
      setError(undefined);
      setEmbedUrl(data.embedUrl);

      // Sigma embed tokens are short-lived. Schedule the next mint just before
      // this one lapses so an analyst reading a dashboard is not interrupted.
      const msUntilRefresh =
        new Date(data.expiresAt).getTime() - Date.now() - REFRESH_LEAD_MS;
      if (msUntilRefresh > 0) {
        refreshTimer.current = setTimeout(loadEmbed, msUntilRefresh);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response
        ?.status;
      // Fail closed. There is deliberately no fallback dashboard: showing another
      // institution's analytics would be worse than showing none.
      setEmbedUrl(undefined);
      if (status === 404) {
        setError("Analytics has not been configured for this organization.");
      } else if (status === 401 || status === 403) {
        // Permanent for this user — retrying will not help, so do not imply it.
        setError("You do not have access to analytics for this organization.");
      } else {
        setError(
          "Analytics is temporarily unavailable. Please try again shortly."
        );
      }
    }
  }, [orgSlug]);

  useEffect(() => {
    loadEmbed();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [loadEmbed]);

  // These states sit inside a `bg-secondary` wrapper sized for the iframe.
  // `text-muted` is the same grey as `bg-secondary` in Bootstrap 4, so give
  // them their own light surface — otherwise the message is invisible and a
  // fail-closed page reads as a blank screen.
  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 bg-white">
        <p className="text-muted mb-0">{error}</p>
      </div>
    );
  }

  if (!embedUrl) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 bg-white">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading analytics…</span>
        </div>
      </div>
    );
  }

  return (
    <iframe
      title="Analytics"
      src={embedUrl}
      frameBorder="0"
      width="100%"
      height="100%"
    />
  );
};

export const AnalyticsRouter = () => {
  let { path } = useRouteMatch();
  const organization = useSelector(selectOrg);
  const user = useSelector(selectMyUser);

  // Mirror the API's AnalyticsPolicy, which allows portalAdmin and analyst but
  // not portalUser. `isAdminPortalUser` is broader — it includes portalUser —
  // so gating on it would let a portalUser render a frame the mint endpoint
  // then refuses with a 403. The API enforces this regardless; matching it here
  // avoids showing a transient-error message for a permanent denial.
  if (!user?.roles || !canAccess(user.roles, ["portalAdmin", "analyst"])) {
    return <Unauthorized userId={user!.userId} />;
  }

  return (
    <Switch>
      <PrivateRoute exact path={`${path}`}>
        <div className="bg-secondary" style={{ height: "93vh" }}>
          {organization?.slug && (
            <AnalyticsEmbedFrame orgSlug={organization.slug} />
          )}
        </div>
      </PrivateRoute>
    </Switch>
  );
};
